// ─── ValidateTab ──────────────────────────────────────────────────────────────
// Data Analytics → Validate sub-tab (XD layout + workflow).
//   Toolbar: zoom, status pills (show/hide colouring), and 5 right-side tools:
//     🔍 Find and Mark · 📋 Mark Rules · ➕ Add Comment · 💬 Comments · 🕘 History
//   Chart (real traces) above a full spreadsheet of real data with cells coloured
//   by QA/QC status (from manual Find-and-Mark + saved Mark Rules).
//
// Persistence: marks / rules / comments / history are saved to localStorage so
// they survive reload (per browser). Server-side/shared persistence would need
// backend tables + endpoints (not present yet).
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useMemo, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Spreadsheet from 'react-spreadsheet'
import { ChevronDown, Search, ClipboardList, MessageSquarePlus, MessageSquare, History, X, Pencil, MoreVertical, FileText, FileSpreadsheet, Download, Printer, Trash2 } from 'lucide-react'
import { DynamicGraph } from '../../DynamicGraph'
import { useVisualizeData } from './useVisualizeData'
import { loadValidate, saveValidate, markKey, matchCondition, CONDITIONS, uid } from './validatePersistence'
import { applyMarkRule, applyMarkByCells, updateCellValues, fetchMarks, fetchRules, createOnGoingRule, patchRule, deleteRule, createComment, fetchComments, fetchValidationVisibility, saveValidationVisibility } from '../../../lib/validateApi'
import useAuthStore from '../../../store/auth-store'
import { resolveIanaTz, wallClockPartsInTz } from '../../../lib/tz'

function csvEscape(v) {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

// Label precision per view — must match INTERVAL_PRECISION in useVisualizeData.js so
// the marks we load key onto the same cell labels the grid renders. All four
// views render raw 10-minute samples, so every label keeps minute precision.
const PERIOD_LABEL_PRECISION = { D: 'minute', W: 'minute', M: 'minute', Y: 'minute' }

const COL_W = 150, ROW_H = 28, ROWHDR_W = 44, MIN_ROWS = 50, HEADER_ROWS = 3
// The sheet fills the space under the chart and scrolls internally; fitRows
// (from the measured height) decides how many rows render, so no fixed height
// is needed. MIN_ROWS is the floor when the measured height is very small.

// `definition` text is shown inside the pill's dropdown menu (Option C
// design: Show / Hide buttons + a Definition section with Learn More link).
// Definitions for Outlier / Suspect / Erroneous are taken verbatim from the
// XD prototype (DataAnalytics_Validate, screen 99) tooltip popovers.
// TODO: confirm exact wording for reviewed / verified / corrected / backfilled
// with the design team — the wording below is inferred from standard data-
// quality taxonomy and is intentionally short to match the XD pattern.
const STATUS_CONFIG = [
    { id: 'provisional', label: 'Provisional', bg: '#FFFFFF', text: '#374151', border: '#D1D5DB', dropdown: false,
      definition: 'Provisional data has not yet been reviewed or marked.' },
    { id: 'outlier',     label: 'Outlier',     bg: '#E8E89F', text: '#4D4D4D', border: '#F1D862',
      definition: 'Outlier data is significantly higher or lower than the general trend.' },
    { id: 'suspect',     label: 'Suspect',     bg: '#F4D0AD', text: '#4D4D4D', border: '#E89A57',
      definition: 'Suspect data is potentially inaccurate.' },
    { id: 'erroneous',   label: 'Erroneous',   bg: '#F0D1D3', text: '#4D4D4D', border: '#E3776E',
      definition: 'Erroneous data is inaccurate.' },
    { id: 'reviewed',    label: 'Reviewed',    bg: '#DBCFED', text: '#4D4D4D', border: '#BD93CC',
      definition: 'Reviewed data has been visually inspected by a qualified user.' },
    { id: 'verified',    label: 'Verified',    bg: '#B1DEB1', text: '#4D4D4D', border: '#7DC57A',
      definition: 'Verified data has been confirmed against a known reference.' },
    { id: 'corrected',   label: 'Corrected',   bg: '#C4DCF0', text: '#4D4D4D', border: '#7AAFE0',
      definition: 'Corrected data has been adjusted to remove a known error.' },
    { id: 'backfilled',  label: 'Backfilled',  bg: '#D6D6D6', text: '#4D4D4D', border: '#BDBDBD',
      definition: 'Backfilled data has been inserted to fill a gap in the record.' },
]
// URL the "Learn More" link in each pill's dropdown points to. Single
// destination shared by all statuses — the Help Center article that
// documents the data-validation taxonomy. Update once the doc is published.
const STATUS_LEARN_MORE_URL = 'https://wqdatalive.com/docs/validation-statuses'
// Mark As offers all 8 statuses (spec). Marking as "provisional" clears the mark.
const MARK_AS = STATUS_CONFIG
// Timestamp stamp used in comments + history entries.  Matches the design
// screenshot ("01/20/2026 1:45pm EST"): zero-padded MM/DD/YYYY, 12-h clock
// with no seconds, lowercase am/pm with no space, browser TZ abbreviation.
const now = () => {
    const d   = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    let h     = d.getHours()
    const ampm = h >= 12 ? 'pm' : 'am'
    h = h % 12 || 12
    let tzAbbr = ''
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(d)
        tzAbbr = (parts.find((p) => p.type === 'timeZoneName') || {}).value || ''
    } catch { /* ignore */ }
    return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${h}:${pad(d.getMinutes())}${ampm}${tzAbbr ? ' ' + tzAbbr : ''}`
}
const colLetter = (i) => { let s = ''; i += 1; while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26) } return s }

export default function ValidateTab({ projectId = null, selectedParameters = [], dateRangeGraph, activePeriod, externalRefreshNonce = 0, mobileMenuSlot = null }) {
    const [zoom, setZoom] = useState('100%')
    // Drag on the graph highlights a time range; when set, the spreadsheet
    // below is filtered to only the rows within [from, to] (epoch ms). The
    // chart itself is NOT zoomed — it still shows the full period. Users
    // clear the selection via the "Clear Selection" button in the banner.
    const [graphRange, setGraphRange] = useState(null)
    const [enabledStatuses, setEnabledStatuses] = useState(Object.fromEntries(STATUS_CONFIG.map((s) => [s.id, true])))

    // The Show/Hide pills are a SHARED, per-project rule (persisted server-side),
    // not local state — so hiding a status excludes it everywhere the project's
    // data is read. Load the saved rule on mount; toggling persists it.
    useEffect(() => {
        if (!projectId) return
        let cancelled = false
        fetchValidationVisibility(projectId)
            .then((res) => {
                if (cancelled) return
                const hidden = new Set((res?.hidden_statuses || []).map((s) => String(s).toLowerCase()))
                setEnabledStatuses(Object.fromEntries(STATUS_CONFIG.map((s) => [s.id, !hidden.has(s.id)])))
            })
            .catch(() => { /* keep the all-visible default on error */ })
        return () => { cancelled = true }
    }, [projectId])

    // Save the hidden set (derived from the pills) to the shared per-project rule.
    const persistVisibility = (nextEnabled) => {
        if (!projectId) return
        const hidden = STATUS_CONFIG.filter((s) => nextEnabled[s.id] === false).map((s) => s.id)
        saveValidationVisibility(projectId, hidden).catch(() => { /* non-fatal; UI already updated */ })
    }
    // Corrected has a 3-option dropdown ("Show Corrected / Show Original / Hide"),
    // and the user can pick which of the two values to display for Corrected
    // cells. Other statuses don't carry an original/corrected distinction.
    // `false` (default) → show the (possibly-corrected) `value` column.
    // `true`            → show `original_value` instead.
    const [correctedShowsOriginal, setCorrectedShowsOriginal] = useState(false)
    // Which status pill's Show/Hide menu is currently open (null = none).
    // Per the XD design, each chevron opens a tiny menu with two items:
    // "Show" and "Hide". Provisional has no chevron and no menu.
    const [openStatusMenu, setOpenStatusMenu] = useState(null)
    const statusMenuRef = useRef(null)
    // Mobile only: the 8 pills collapse behind a "Status ▾" dropdown (matches the
    // XD). statusPanelRef wraps that control so clicks inside it don't close the
    // per-pill Show/Hide menu, and clicks outside close the panel.
    const [statusPanelOpen, setStatusPanelOpen] = useState(false)
    const statusPanelRef = useRef(null)
    // The ⋮ overflow menu (export/print) — shown in the tab-row group on mobile
    // (portalled into mobileMenuSlot) to match the XD.
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef(null)
    useEffect(() => {
        if (!menuOpen) return
        const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false) }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [menuOpen])
    useEffect(() => {
        if (!openStatusMenu) return
        const close = (e) => {
            const inDesktop = statusMenuRef.current && statusMenuRef.current.contains(e.target)
            const inPanel   = statusPanelRef.current && statusPanelRef.current.contains(e.target)
            if (!inDesktop && !inPanel) setOpenStatusMenu(null)
        }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [openStatusMenu])
    useEffect(() => {
        if (!statusPanelOpen) return
        const close = (e) => {
            if (statusPanelRef.current && !statusPanelRef.current.contains(e.target)) {
                setStatusPanelOpen(false)
                setOpenStatusMenu(null)
            }
        }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [statusPanelOpen])
    // Right dock: 'comments' | 'history' | null. Open to Comments on desktop (as
    // the XD shows), but closed on a phone so the graph/table get the full width
    // (the dock would otherwise squeeze them — see the mobile XD).
    const [panel, setPanel] = useState(() =>
        (typeof window !== 'undefined' && window.innerWidth < 768) ? null : 'comments'
    )
    const [modal, setModal] = useState(null)          // 'find' | 'rules' | 'comment' | null
    const [vstate, setVstate] = useState(loadValidate)
    const [active, setActive] = useState(null) // active grid cell {row, column}
    // Selected cell range from the spreadsheet's drag-select / shift-click.
    // Used to drive the "Mark Selected as …" menu items inside each status pill.
    // Stored as a list of {row, column} relative to the gridData matrix; the
    // pill action translates each cell into a (parameter_id, sample_time)
    // pair and POSTs to /api/data/marks/apply-cells/.
    const [selectedCells, setSelectedCells] = useState([])
    // Map<cellKey, {comment, ruleId, status, createdAt}> — cells whose marking rule
    // has a `comment` attached. Drives the blue triangle (via `da-comment` CSS
    // class) AND the hover-tooltip content. Populated by the marks-load useEffect.
    const [ruleCommentedKeys, setRuleCommentedKeys] = useState(() => new Map())
    // Map<cellKey, {original_value, value, status, rule_id}> — kept alongside
    // vstate.marks so the grid can swap displayed value ↔ original_value for
    // Corrected cells when the user picks "Show Original" in the dropdown.
    const [markDetailsByKey, setMarkDetailsByKey] = useState(() => new Map())
    // Counter bumped after every successful server-side write (inline edit,
    // Edit-Value modal). Passed to useVisualizeData + DynamicGraph so both the
    // grid and the chart re-pull `Data.value` and reflect the correction —
    // otherwise the existing deps (params/range/period) don't change and the
    // edit only shows up via the local overlay.
    const [refreshNonce, setRefreshNonce] = useState(0)
    const bumpRefresh = () => setRefreshNonce((n) => n + 1)
    // Tracks whether the user has explicitly rolled back to a historical snapshot.
    // When true, the 30-second background poll skips overwriting vstate.marks so
    // the rolled-back marks remain visible. Reset whenever params/range change
    // (the useEffect re-runs) or the user explicitly applies new marks.
    const rolledBackRef = useRef(false)
    // Combine the internal counter (bumped after server writes from this tab)
    // with the shared refresh nonce coming from DataAnalyticsView's toolbar
    // button. Either source flipping forces useVisualizeData and DynamicGraph
    // to re-fetch.
    const effectiveRefreshNonce = refreshNonce + externalRefreshNonce
    // Tooltip state: { kind, payload, x, y } | null
    //   kind='user' → payload = { author, timestamp, body, attachments }
    //   kind='rule' → payload = { body }
    const [hoverComment, setHoverComment] = useState(null)

    // Customer TZ — sent to BOTH the visualize-data fetch (grid) and the
    // marks fetch so the backend interprets the wall-clock from/to dates
    // consistently. Must be declared BEFORE useVisualizeData below — JS
    // temporal-dead-zone rules mean a `const` can't be referenced earlier
    // in the function body. Falls back to browser TZ when no account tz.
    const rawCustomerTz = useAuthStore((s) => s.user?.timezone)
    const customerTz    = useMemo(() => resolveIanaTz(rawCustomerTz), [rawCustomerTz])

    // includeHidden=true: the Validate screen is the curation surface, so it
    // must keep seeing (and be able to un-mark) readings the project has Hidden.
    // Everywhere else the server applies the per-project visibility rule.
    const { data, series, loading } = useVisualizeData(selectedParameters, dateRangeGraph, activePeriod, effectiveRefreshNonce, customerTz, true)
    const zoomFactor = parseInt(zoom, 10) / 100

    // Set of statusIds the user has toggled OFF via the toolbar pills.
    // Passed to DynamicGraph so the chart drops any sample whose mark is in
    // this set — keeps graph + grid colour story in sync.
    const hiddenStatusSet = useMemo(
        () => new Set(Object.entries(enabledStatuses).filter(([, on]) => !on).map(([id]) => id)),
        [enabledStatuses]
    )

    // ── Load server-side marks for the visible parameters + range ────────────
    // Fetches /api/data/marks/ for each selected parameter and seeds
    // `vstate.marks` so the bottom grid paints cells with the status colour
    // (and `da-comment` indicator) on page load.
    //
    // The grid keys cells as `markKey(parameter_id, label)` where label is the
    // wall-clock string the visualize API produces (YYYY-MM-DD HH:MM, browser
    // TZ) from `sample_time` — the timestamp the SENSOR reported, not the
    // server-insert time. We rebuild that same label from `m.sample_time`.
    // (Falling back to `m.creation_time` keeps older backends working but
    // produces a label that may not match the grid for new-style endpoints.)
    //
    // Polls every 30s so cells light up automatically as the cron applies
    // on-going Mark Rules — no manual page reload needed.
    const paramIdsKey = selectedParameters.map((p) => p.id).join(',')
    const rangeFrom   = dateRangeGraph?.from
    const rangeTo     = dateRangeGraph?.to

    useEffect(() => {
        if (!selectedParameters.length || !rangeFrom || !rangeTo) return
        // Params / range / period / refresh-button changed — exit rollback mode
        // so the user gets fresh server data for the new context.
        rolledBackRef.current = false
        let cancelled = false
        const pad = (n) => String(n).padStart(2, '0')
        const precision = PERIOD_LABEL_PRECISION[activePeriod] || 'minute'
        // Labels must use the customer TZ — the grid (useVisualizeData) now
        // formats its row labels in customer TZ via the API's tzOffsetSeconds.
        // If we keep using browser TZ here, the cell key
        // `markKey(paramId, label)` won't line up with the grid's keys when
        // customer TZ ≠ browser TZ, so colours would never paint.
        const labelOf = (utcStr) => {
            const ms = Date.parse(String(utcStr).replace(' ', 'T') + 'Z')
            if (Number.isNaN(ms)) return null
            const parts = wallClockPartsInTz(new Date(ms), customerTz)
            const ymd = `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
            if (precision === 'date') return ymd
            if (precision === 'hour') return `${ymd} ${pad(parts.hour)}:00`
            return `${ymd} ${pad(parts.hour)}:${pad(parts.minute)}`
        }
        const loadOnce = async () => {
            const merged = {}
            const commented = new Map()
            const details   = new Map()
            const userComments = []
            for (const p of selectedParameters) {
                if (!p?.id) continue
                try {
                    const res = await fetchMarks({
                        parameterId: p.id, fromDate: rangeFrom, toDate: rangeTo, timezone: customerTz,
                    })
                    if (cancelled) return
                    for (const m of (res?.marks || [])) {
                        const lbl = labelOf(m.sample_time || m.creation_time)
                        if (!lbl) continue
                        const key = markKey(p.id, lbl)
                        if (m.status) merged[key] = m.status
                        details.set(key, {
                            original_value: m.original_value,
                            value:          m.value,
                            status:         m.status,
                            rule_id:        m.rule_id,
                        })
                        const txt = m.rule_comment && String(m.rule_comment).trim()
                        if (txt) {
                            commented.set(key, { body: txt, ruleId: m.rule_id, status: m.status })
                        }
                    }
                } catch { /* swallowed — one param failure shouldn't blank the grid */ }
                try {
                    const cRes = await fetchComments({
                        parameterId: p.id, fromDate: rangeFrom, toDate: rangeTo, timezone: customerTz,
                    })
                    if (cancelled) return
                    for (const c of (cRes?.comments || [])) {
                        const lbl = labelOf(c.sample_time)
                        if (!lbl) continue
                        userComments.push({
                            id:         c.id,
                            author:     `User #${c.created_by}`,   // TODO: resolve to display name
                            cellRef:    c.cell_ref || '',
                            paramId:    c.parameter_id,
                            label:      lbl,
                            timestamp:  c.created_at,
                            body:       c.body,
                            attachments: (c.attachments || []).map((a) => ({
                                name:    a.filename,
                                dataUrl: a.url,                    // tooltip uses dataUrl for <img src>; URL works the same
                                mime:    a.mime_type,
                                bytes:   a.size_bytes,
                            })),
                        })
                    }
                } catch { /* same — degrade gracefully */ }
            }
            if (cancelled) return
            setVstate((prev) => {
                // User rolled back to a historical snapshot — leave marks alone.
                // The 30-second interval fires silently; it must not undo an
                // explicit rollback. Only cleared when params/range change
                // (effect re-runs with rolledBackRef reset above) or the user
                // applies new marks.
                if (rolledBackRef.current) return prev
                // ── marks: REPLACE for currently-loaded parameters ─────────
                // The server is source of truth for whatever it returns.
                // Previously we merged ({...prev.marks, ...merged}) which
                // caused stale localStorage marks to persist forever — if
                // a mark was deleted on the backend, this browser's grid
                // kept showing the cell coloured because the local map
                // still had the entry and the merge couldn't see "this
                // key is gone, drop it". The fix: drop any prior mark
                // whose paramId is in the set we just refetched, then
                // overlay the fresh server marks. Marks for parameters
                // NOT in this fetch (e.g. ones the user previously viewed
                // but isn't viewing now) are left alone so switching
                // back to them doesn't briefly blank the colours before
                // the next poll re-seeds.
                const paramIdSet = new Set(selectedParameters.map((p) => String(p.id)))
                const carriedOver = {}
                for (const [k, v] of Object.entries(prev.marks || {})) {
                    const [paramId] = k.split('|')
                    if (!paramIdSet.has(paramId)) carriedOver[k] = v
                }
                // ── comments: REPLACE for currently-loaded parameters ──────
                // Same fix as marks above — without this, switching projects
                // left the Comments sidebar showing entries from the previous
                // project (vstate.comments is persisted in localStorage and
                // wasn't scoped by paramId). We now drop any server-side
                // entry whose parameter belongs to this fetch and is missing
                // from the response, while keeping:
                //   – Optimistic entries (string ids from uid('c')) so a
                //     just-added comment doesn't flicker out before the
                //     next 30 s poll confirms it.
                //   – Server entries for parameters NOT in this fetch — i.e.
                //     comments tied to params the user previously viewed in
                //     a different project. Those are still trimmed by the
                //     project-switch flow at the top of DashboardPage.
                const incomingIds = new Set(userComments.map((c) => c.id).filter((v) => v != null))
                const kept = (prev.comments || []).filter((c) => {
                    // Optimistic — always keep.
                    if (typeof c.id === 'string') return true
                    // Server entry already replaced by this fetch.
                    if (incomingIds.has(c.id)) return false
                    // Server entry for a param we just refetched but isn't
                    // in the response → it's stale (deleted on the server,
                    // out of new date range, or belonged to a different
                    // project's grid). Drop it.
                    if (paramIdSet.has(String(c.paramId))) return false
                    // Server entry for a param not in this fetch — keep.
                    return true
                })
                return {
                    ...prev,
                    marks:    { ...carriedOver, ...merged },
                    comments: [...userComments, ...kept],
                }
            })
            setRuleCommentedKeys(commented)
            setMarkDetailsByKey(details)
        }
        loadOnce()
        const intervalId = setInterval(loadOnce, 30 * 1000)
        return () => { cancelled = true; clearInterval(intervalId) }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramIdsKey, rangeFrom, rangeTo, activePeriod, customerTz, externalRefreshNonce, refreshNonce])

    // Each history entry snapshots the state so it can be rolled back to.
    const commit = (next, historyText) => {
        const merged = historyText
            ? { ...next, history: [{ id: uid('h'), time: now(), text: historyText, snapshot: { marks: next.marks, rules: next.rules, comments: next.comments } }, ...(next.history || [])] }
            : next
        setVstate(merged); saveValidate(merged)
    }
    const rollback = (entry) => {
        if (!entry.snapshot) return
        rolledBackRef.current = true
        commit({ marks: entry.snapshot.marks || {}, rules: entry.snapshot.rules || [], comments: entry.snapshot.comments || [], history: vstate.history }, `Rolled back to: ${entry.text}`)
    }

    const sheetRef = useRef(null)
    const [box, setBox] = useState({ w: 0, h: 0 })
    useEffect(() => {
        const el = sheetRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver((e) => { const cr = e[0].contentRect; setBox({ w: cr.width, h: cr.height }) })
        ro.observe(el); return () => ro.disconnect()
    }, [])

    // Effective status for a value cell: manual mark wins, else first matching rule.
    const statusFor = (paramId, label, value) => {
        const m = vstate.marks[markKey(paramId, label)]
        if (m) return m
        for (const r of vstate.rules) {
            if (r.paramId === String(paramId) && matchCondition(value, r.condition, r.v1, r.v2)) return r.status
        }
        return null
    }

    // The graph-drag no longer filters (or zooms). ALL rows stay in the sheet;
    // a drag-selection instead SCROLLS the sheet so the highlighted range sits
    // at the top (see the scroll effect below). Kept as a pass-through so the
    // downstream gridData/render pipeline is unchanged.
    const filteredData = useMemo(() => data, [data])

    // When the graph selection changes, bring the FIRST selected row to the top
    // of the table viewport. Rows are sorted ascending, so the first match
    // scanning top-down is the earliest reading in the range (the start of the
    // highlighted block). We scroll whatever element is actually the vertical
    // scroller (the page/`dash-main` now, not the sheet itself), and skip if the
    // row is already fully visible so we don't scroll unnecessarily.
    useEffect(() => {
        if (!graphRange || graphRange.from == null || graphRange.to == null) return
        const container = sheetRef.current
        if (!container) return
        const lo = Math.min(graphRange.from, graphRange.to)
        const hi = Math.max(graphRange.from, graphRange.to)
        let target = -1
        for (let i = HEADER_ROWS; i < data.length; i++) {
            const ms = data[i]?.[0]?.ms
            if (typeof ms === 'number' && ms >= lo && ms <= hi) { target = i; break }
        }
        if (target < 0) return
        // Let the grid paint first. rows[0] is react-spreadsheet's column-header
        // row, so data row i renders as rows[i + 1].
        const id = requestAnimationFrame(() => {
            const rows = container.querySelectorAll('table tr')
            const tr = rows[target + 1]
            if (!tr) return
            // Walk up to the nearest ancestor that actually scrolls vertically.
            let sc = tr.parentElement
            while (sc && sc !== document.body) {
                const oy = getComputedStyle(sc).overflowY
                if ((oy === 'auto' || oy === 'scroll') && sc.scrollHeight > sc.clientHeight + 1) break
                sc = sc.parentElement
            }
            const rRect = tr.getBoundingClientRect()
            const OFFSET = 8  // small gap above the row
            if (!sc || sc === document.body || sc === document.documentElement) {
                // Fallback: no bounded scroll ancestor — let the browser handle it.
                const vh = window.innerHeight || document.documentElement.clientHeight
                if (rRect.top >= 90 && rRect.bottom <= vh) return  // already visible
                tr.scrollIntoView({ block: 'start', behavior: 'smooth' })
                return
            }
            const cRect = sc.getBoundingClientRect()
            // Already fully visible in the scroll viewport → don't scroll.
            if (rRect.top >= cRect.top && rRect.bottom <= cRect.bottom) return
            sc.scrollTo({ top: sc.scrollTop + (rRect.top - cRect.top) - OFFSET, behavior: 'smooth' })
        })
        return () => cancelAnimationFrame(id)
    }, [graphRange, data])

    const gridData = useMemo(() => {
        const data = filteredData
        if (!data.length) return []
        // When a range is dragged on the graph, wash every data row whose
        // sample time falls inside it so the table selection mirrors the
        // chart highlight (in addition to the scroll-into-view above).
        const gr = (graphRange && graphRange.from != null && graphRange.to != null)
            ? [Math.min(graphRange.from, graphRange.to), Math.max(graphRange.from, graphRange.to)]
            : null
        const dataCols = data.reduce((m, r) => Math.max(m, r.length), 0)
        const blank = { value: '', readOnly: true }
        // totalCols stays glued to the actual data width — previously we padded
        // out to fitCols (viewport-driven) which produced an empty stripe on
        // the right of the grid.
        const totalCols = Math.max(dataCols, 1)
        // Rows pad to the data (or a small minimum) ONLY — no viewport-height
        // fill. The sheet grows to its content and the page scrolls as one,
        // instead of the grid getting its own inner vertical scrollbar.
        const totalRows = Math.max(data.length, MIN_ROWS)
        // Union: cells with a user-added inline comment (vstate.comments) OR
        // cells tagged by a rule that itself carries a comment (ruleCommentedKeys).
        // Both get the same `da-comment` blue-triangle treatment in the corner.
        const commentedKeys = new Set([
            ...vstate.comments.map((c) => markKey(c.paramId, c.label)),
            ...ruleCommentedKeys.keys(),
        ])
        const out = []
        for (let r = 0; r < totalRows; r++) {
            const src = data[r]
            const row = new Array(totalCols)
            // Datetime cell (col 0) carries the row's epoch-ms (see
            // useVisualizeData); header rows have none, so they never highlight.
            const rowMs = src?.[0]?.ms
            const inGraphRange = !!gr && typeof rowMs === 'number' && rowMs >= gr[0] && rowMs <= gr[1]
            // Cells are always read-only — value edits go through the pencil
            // (Edit-Value) modal so the user sees the current value, can type
            // the replacement deliberately, and confirms with a Save click.
            for (let c = 0; c < totalCols; c++) {
                const cell = src && src[c]
                const base = { value: cell?.value ?? '', readOnly: true }
                if (cell && r >= HEADER_ROWS && c >= 1 && cell.value !== '' && !isNaN(Number(cell.value))) {
                    const param = series[c - 1]
                    const label = src[0]?.value
                    const st = param ? statusFor(param.id, label, Number(cell.value)) : null
                    // "Show Original" toggle on the Corrected pill: swap the
                    // displayed numeric for `original_value` whenever this
                    // particular cell carries a Corrected mark and the user
                    // has flipped the pill into Show-Original mode. Other
                    // statuses always display the current `value` column.
                    let displayValue = base.value
                    const isCorrectedShowingOriginal = st === 'corrected' && correctedShowsOriginal
                    // "Show Original" toggle on the Corrected pill: swap the
                    // displayed numeric for `original_value` whenever this
                    // particular cell carries a Corrected mark and the user
                    // has flipped the pill into Show-Original mode. Other
                    // statuses always display the current `value` column.
                    if (isCorrectedShowingOriginal && param) {
                        const det = markDetailsByKey.get(markKey(param.id, label))
                        if (det && det.original_value != null) {
                            // Preserve the cell's existing precision so the
                            // grid looks consistent with the surrounding rows.
                            const cur = String(base.value)
                            const dotIdx = cur.indexOf('.')
                            const decimals = dotIdx >= 0 ? cur.length - dotIdx - 1 : 0
                            displayValue = Number(det.original_value).toFixed(decimals)
                        }
                    }
                    const cls = [
                        st && enabledStatuses[st] ? `da-${st}` : '',
                        param && commentedKeys.has(markKey(param.id, label)) ? 'da-comment' : '',
                        inGraphRange ? 'da-graph-range' : '',
                    ].filter(Boolean).join(' ')
                    const cellOut = { ...base, value: displayValue }
                    row[c] = cls ? { ...cellOut, className: cls } : cellOut
                } else {
                    const b = cell ? base : blank
                    row[c] = inGraphRange ? { ...b, className: 'da-graph-range' } : b
                }
            }
            out.push(row)
        }
        return out
    }, [filteredData, series, vstate, enabledStatuses, ruleCommentedKeys, markDetailsByKey, correctedShowsOriginal, box.w, box.h, zoomFactor, graphRange])

    // (Inline grid editing was removed — edits go through the pencil modal.)
    // Helper kept for the modal: optimistic local apply + POST + refresh.
    const commitInlineEdit = async (paramId, label, newValue, preEditValue) => {
        const key = markKey(paramId, label)
        // Optimistic status flip so the grid colour updates instantly while
        // the POST is in flight; the cell value itself refreshes from the
        // server after bumpRefresh().
        setVstate((prev) => ({ ...prev, marks: { ...prev.marks, [key]: 'corrected' } }))
        // Seed markDetailsByKey so "Show Original" on the Corrected pill
        // reveals the pre-edit value RIGHT AWAY — without this the toggle
        // would do nothing until the next 30-s marks-poll arrives with
        // the server-side original_value. Only seed if there's no prior
        // entry: if this row was already corrected once, the existing
        // original_value (= true device value) must be preserved.
        if (preEditValue !== undefined && preEditValue !== '') {
            setMarkDetailsByKey((prev) => {
                if (prev.has(key) && prev.get(key)?.original_value != null) return prev
                const m = new Map(prev)
                m.set(key, {
                    original_value: Number(preEditValue),
                    value:          Number(newValue),
                    status:         'corrected',
                    rule_id:        null,
                })
                return m
            })
        }
        const sampleTime = /:[0-9]{2}$/.test(String(label)) ? `${label}:00` : String(label)
        try {
            await updateCellValues({
                parameterId: paramId,
                sampleTimes: [sampleTime],
                newValue:    Number(newValue),
                status:      'corrected',
                timezone:    customerTz,
            })
            // Server confirmed → force grid + chart to re-pull so the new
            // value lands in the underlying dataset (the optimistic overlay
            // already showed it; this clears the overlay implicitly via the
            // useEffect that resets it on dep change).
            bumpRefresh()
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || e
            alert(`Save failed for ${sampleTime}: ${msg}`)
        }
    }

    // Apply a (paramId, condition, values, status) over the loaded data → marks.
    const applyMark = (paramId, condition, v1, v2, status, ruleName) => {
        const col = series.findIndex((s) => String(s.id) === String(paramId)) + 1
        if (col <= 0) return 0
        const marks = { ...vstate.marks }
        let count = 0
        for (let r = HEADER_ROWS; r < data.length; r++) {
            const v = data[r]?.[col]?.value
            const label = data[r]?.[0]?.value
            if (v !== '' && !isNaN(Number(v)) && matchCondition(Number(v), condition, v1, v2)) {
                if (status === 'provisional') delete marks[markKey(paramId, label)] // clear → raw
                else marks[markKey(paramId, label)] = status
                count++
            }
        }
        const pname = series[col - 1]?.name || paramId
        rolledBackRef.current = false  // user is applying find-and-mark → exit rollback mode
        commit({ ...vstate, marks }, `${ruleName || 'Find & Mark'}: marked ${count} point(s) of "${pname}" as ${status}`)
        return count
    }

    const togglePill = (id) => setEnabledStatuses((p) => { const next = { ...p, [id]: !p[id] }; persistVisibility(next); return next })

    // Group `selectedCells` by parameter_id, look up each cell's sample_time
    // from the matrix, POST one bulk-mark per parameter.  Today the grid's
    // labels are wall-clock in browser TZ at minute precision — only D view
    // gives a 1-to-1 mapping back to underlying Data.sample_time. For W/M/Y
    // (aggregated buckets), the user gets an alert because tagging a bucket
    // would silently mark a different set of rows than they expect.
    const markSelectedCellsAs = async (statusId) => {
        if (!selectedCells.length) {
            alert('Select one or more value cells in the grid first, then choose "Mark Selected as …".')
            return
        }
        // Group cells by parameter and collect sample_time strings. The data
        // matrix's first column holds the (browser-TZ) label like "2026-06-15 08:30".
        const byParam = new Map() // paramId → [sample_time_str, ...]
        for (const { row, column } of selectedCells) {
            const param = series[column - 1]
            const label = data[row] && data[row][0] && data[row][0].value
            if (!param || !label) continue
            // Reconstruct a 'YYYY-MM-DD HH:MM:00' string the backend can parse —
            // it expects seconds; cells render without them.
            const sampleStr = /:[0-9]{2}$/.test(String(label)) ? `${label}:00` : String(label)
            if (!byParam.has(param.id)) byParam.set(param.id, [])
            byParam.get(param.id).push(sampleStr)
        }
        if (byParam.size === 0) {
            alert('No selected cells map to a value column.')
            return
        }
        let totalAffected = 0
        try {
            for (const [paramId, sampleTimes] of byParam.entries()) {
                const res = await applyMarkByCells({
                    parameterId: paramId,
                    status:      statusId,
                    sampleTimes,
                    timezone:    customerTz,
                })
                totalAffected += (res?.affected_rows || 0)
            }
            // Optimistic local update so the user sees colour immediately,
            // even before the 30-s marks-poll refresh fires.
            rolledBackRef.current = false  // user is marking new cells → exit rollback mode
            const optimistic = { ...vstate.marks }
            const paramNames = [...new Set(
                selectedCells.map(({ column }) => series[column - 1]?.name).filter(Boolean)
            )].join(', ')
            for (const { row, column } of selectedCells) {
                const param = series[column - 1]
                const label = data[row] && data[row][0] && data[row][0].value
                if (param && label) optimistic[markKey(param.id, label)] = statusId
            }
            const histText = `Marked ${totalAffected} cell(s) as ${statusId}${paramNames ? ` — ${paramNames}` : ''}`
            setVstate((prev) => {
                const entry = {
                    id: uid('h'),
                    time: now(),
                    text: histText,
                    snapshot: { marks: optimistic, rules: prev.rules, comments: prev.comments },
                }
                const merged = { ...prev, marks: optimistic, history: [entry, ...(prev.history || [])] }
                saveValidate(merged)
                return merged
            })
            // Action complete — drop the selection so the next click starts
            // a fresh pick and the pills aren't aimed at the cells we just
            // marked. Mirrors the post-save behaviour of inline edits and
            // the Edit-Value modal.
            setSelectedCells([])
            alert(`Marked ${totalAffected} cell(s) as ${statusId}.`)
        } catch (e) {
            alert(`Mark failed: ${e?.response?.data?.message || e?.message || e}`)
        }
        setOpenStatusMenu(null)
    }
    const hasParams = selectedParameters.length > 0
    const iconBtn = (active) => `p-1 md:p-1.5 rounded ${active ? 'text-[#358ECD] bg-[#E8F1F8]' : 'text-gray-600 hover:bg-gray-100'}`

    // ── ⋮ overflow menu: export the validation grid ────────────────────────────
    // Same actions as the Visualize tab's menu, operating on the aligned grid
    // matrix (`data`): row 0..HEADER_ROWS-1 are Site/Device/Parameter headers.
    const hasExportData = data.length > 0
    const downloadCsv = () => {
        const csv = data.map((row) => row.map((cell) => csvEscape(cell?.value)).join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'validate-data.csv'
        a.click()
        URL.revokeObjectURL(url)
        setMenuOpen(false)
    }
    const downloadXls = async () => {
        const { utils, writeFile } = await import('xlsx')
        const wsData = data.map((row) => row.map((cell) => cell?.value ?? ''))
        const ws = utils.aoa_to_sheet(wsData)
        const wb = utils.book_new()
        utils.book_append_sheet(wb, ws, 'Data')
        writeFile(wb, 'validate-data.xlsx')
        setMenuOpen(false)
    }
    const downloadPdf = async () => {
        if (!data.length) return
        const { jsPDF } = await import('jspdf')
        const { autoTable } = await import('jspdf-autotable')
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
        const pageW = doc.internal.pageSize.getWidth()
        const MARGIN = 36
        doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(20)
        doc.text('Data Analytics — Validate', pageW / 2, MARGIN + 8, { align: 'center' })
        const [siteRow, deviceRow, paramRow, ...dataRows] = data
        const cellText = (row) => row.map((c) => c?.value ?? '')
        autoTable(doc, {
            startY: MARGIN + 32,
            margin: { left: MARGIN, right: MARGIN },
            styles: { fontSize: 8, cellPadding: 2.5, textColor: 40, overflow: 'linebreak' },
            headStyles: { fillColor: [245, 245, 245], textColor: 30, halign: 'center', fontStyle: 'bold' },
            columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
            theme: 'grid',
            head: [cellText(siteRow), cellText(deviceRow), cellText(paramRow)].filter(Boolean),
            body: dataRows.map(cellText),
        })
        doc.save('validate-data.pdf')
        setMenuOpen(false)
    }

    const overflowMenu = (
        <div className="flex items-center gap-1 relative" ref={menuRef}>
            <button type="button" onClick={() => setMenuOpen((o) => !o)} className="p-1 text-gray-500 hover:bg-gray-100 rounded">
                <MoreVertical className="w-5 h-5" />
            </button>
            {menuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-30 w-56 py-1 text-sm">
                    <button onClick={downloadPdf} disabled={!hasExportData} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <FileText className="w-4 h-4 text-gray-500 shrink-0" /> Download PDF
                    </button>
                    <button onClick={downloadCsv} disabled={!hasExportData} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <Download className="w-4 h-4 text-gray-500 shrink-0" /> Download CSV
                    </button>
                    <button onClick={downloadXls} disabled={!hasExportData} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <FileSpreadsheet className="w-4 h-4 text-gray-500 shrink-0" /> Download XLS
                    </button>
                    <button onClick={() => { window.print(); setMenuOpen(false) }} disabled={!hasExportData} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <Printer className="w-4 h-4 text-gray-500 shrink-0" /> Print
                    </button>
                </div>
            )}
        </div>
    )

    // One status pill — shared by the desktop inline row and the mobile
    // "Status ▾" dropdown (stacked=true → full-width rows, label left / chevron
    // right) so the two layouts can't drift.
    const renderStatusPill = (s, stacked = false) => {
        const enabled = enabledStatuses[s.id]
        const hasMenu = s.dropdown !== false
        const open    = openStatusMenu === s.id
        const setVisibility = (visible) => {
            setEnabledStatuses((p) => { const next = { ...p, [s.id]: visible }; persistVisibility(next); return next })
            setOpenStatusMenu(null)
        }
        return (
            <div key={s.id} className={`relative ${stacked ? 'w-full' : ''}`}>
                {/* Pill body and chevron are two distinct hit-targets:
                    – Body  : if cells are selected → mark them as this status
                              (one-click workflow); otherwise toggle show/hide
                              (or open the menu if the pill has no plain toggle).
                    – Chevron: always opens the Show/Hide/Mark-Selected dropdown
                              so power-users still have the explicit menu. */}
                <div
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs common-size15 text-[#4D4D4D] transition cursor-pointer select-none ${stacked ? 'w-full justify-between' : ''}`}
                    style={{ backgroundColor: enabled ? s.bg : '#FFFFFF', color: s.text, borderColor: s.border, opacity: enabled ? 1 : 0.5 }}
                    title={(() => {
                        const action = selectedCells.length > 0
                            ? `Click to mark ${selectedCells.length} selected cell(s) as ${s.label}.`
                            : (hasMenu
                                ? 'Click for Show / Hide options.'
                                : (enabled ? 'Click to hide.' : 'Click to show.'))
                        return s.definition ? `${s.definition}\n\n${action}` : action
                    })()}
                >
                    <button
                        type="button"
                        onClick={() => {
                            if (selectedCells.length > 0) {
                                markSelectedCellsAs(s.id)
                            } else if (hasMenu) {
                                setOpenStatusMenu(open ? null : s.id)
                            } else {
                                togglePill(s.id)
                            }
                        }}
                        className="bg-transparent border-0 p-0 m-0 cursor-pointer"
                        style={{ color: 'inherit', font: 'inherit' }}
                    >
                        {s.label}
                    </button>
                    {hasMenu && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setOpenStatusMenu(open ? null : s.id) }}
                            title="Show / Hide / Mark Selected"
                            className="bg-transparent border-0 p-0 m-0 cursor-pointer flex items-center"
                            style={{ color: 'inherit' }}
                        >
                            <ChevronDown
                                className="w-3 h-3 transition-transform"
                                style={{ transform: open ? 'rotate(180deg)' : undefined }}
                            />
                        </button>
                    )}
                </div>
                {hasMenu && open && (
                    // Dropdown intentionally stripped to JUST the visibility options.
                    <div
                        role="menu"
                        className="absolute left-0 top-full mt-1 z-30 w-40 bg-white border border-gray-200 rounded-md shadow-lg py-1"
                    >
                        {s.id === 'corrected' ? (
                            <>
                                <button
                                    type="button" role="menuitem"
                                    onClick={() => { setCorrectedShowsOriginal(false); setVisibility(true) }}
                                    className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 ${enabled && !correctedShowsOriginal ? 'text-[#358ECD]' : 'text-[#4D4D4D]'}`}
                                >
                                    Show Corrected
                                </button>
                                <button
                                    type="button" role="menuitem"
                                    onClick={() => { setCorrectedShowsOriginal(true);  setVisibility(true) }}
                                    className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 ${enabled &&  correctedShowsOriginal ? 'text-[#358ECD]' : 'text-[#4D4D4D]'}`}
                                >
                                    Show Original
                                </button>
                                <button
                                    type="button" role="menuitem"
                                    onClick={() => setVisibility(false)}
                                    className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 ${!enabled ? 'text-[#358ECD]' : 'text-[#4D4D4D]'}`}
                                >
                                    Hide
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button" role="menuitem"
                                    onClick={() => setVisibility(true)}
                                    className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 ${enabled ? 'text-[#358ECD]' : 'text-[#4D4D4D]'}`}
                                >
                                    Show
                                </button>
                                <button
                                    type="button" role="menuitem"
                                    onClick={() => setVisibility(false)}
                                    className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 ${!enabled ? 'text-[#358ECD]' : 'text-[#4D4D4D]'}`}
                                >
                                    Hide
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-3 flex-1 px-4 pb-4 min-h-0" style={{ fontFamily: "'Open Sans', sans-serif" }}>
            {/* Mobile: the ⋮ overflow menu lives in the tab-row group beside the
                calendar (portalled from DataAnalyticsView), matching the XD. */}
            {mobileMenuSlot && createPortal(<div className="md:hidden">{overflowMenu}</div>, mobileMenuSlot)}

            {/* Toolbar — no undo/redo (matches XD). On mobile the zoom is hidden
                and the 8 status pills collapse behind a "Status ▾" dropdown so the
                row stays on one line (matches the XD); desktop is unchanged. */}
            <div className="flex items-center gap-3 bg-[#F1F2F4] rounded-lg px-3 py-2 md:flex-wrap">
                <select value={zoom} onChange={(e) => setZoom(e.target.value)} className="hidden md:block bg-gray-50 border border-gray-200 px-2 py-1 rounded text-xs text-[#4D4D4D] hover:bg-gray-100 focus:outline-none flex-shrink-0">
                    {['50%', '75%', '100%', '125%', '150%'].map((z) => <option key={z} value={z}>{z}</option>)}
                </select>

                {/* Desktop: status pills inline. */}
                <div className="hidden md:flex items-center gap-1.5 flex-wrap pl-10" ref={statusMenuRef}>
                    {STATUS_CONFIG.map((s) => renderStatusPill(s))}
                </div>

                {/* Mobile: the pills collapse behind a Status ▾ dropdown. */}
                <div className="md:hidden relative flex-shrink-0" ref={statusPanelRef}>
                    <button
                        type="button"
                        onClick={() => setStatusPanelOpen((o) => !o)}
                        className="flex items-center gap-1 font-semibold common-size15 px-1 py-1.5 text-[#4D4D4D]"
                    >
                        Status
                        <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ transform: statusPanelOpen ? 'rotate(180deg)' : undefined }} />
                    </button>
                    {statusPanelOpen && (
                        <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex flex-col gap-1.5 min-w-[190px]">
                            {STATUS_CONFIG.map((s) => renderStatusPill(s, true))}
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-1 ml-auto flex-shrink-0">
                    {/* Visual separator between the status-pill group and the
                        action-icon group — matches the XD reference design. */}
                    <span className="mx-2 h-5 w-px bg-gray-300" aria-hidden="true" />
                    {/* Refresh button moved up to DataAnalyticsView's toolbar
                        (next to D/W/M/Y) so it's shared across Visualize /
                        Validate / Formulas. */}
                    <button type="button" title="Find and Mark" onClick={() => setModal('find')} className={iconBtn(modal === 'find')}><Search className="w-5 h-5 text-gray-600" /></button>
                    <button type="button" title="Mark Rules" onClick={() => setModal('rules')} className={iconBtn(modal === 'rules')}><ClipboardList className="w-5 h-5 text-gray-600" /></button>
                    {/* Edit Value — enabled only when one or more data cells are
                        selected. Opens a modal that writes Data.value for those
                        cells; Data.original_value is never touched. */}
                    {(() => {
                        const havePicks = selectedCells.length > 0
                        return (
                            <button
                                type="button"
                                title={havePicks ? `Edit value for ${selectedCells.length} selected cell(s)` : 'Select one or more value cells first, then click to edit'}
                                onClick={() => {
                                    if (!havePicks) { alert('Select one or more value cells in the grid first.'); return }
                                    setModal('edit-value')
                                }}
                                className={iconBtn(modal === 'edit-value') + (havePicks ? '' : ' opacity-40 cursor-not-allowed')}
                                aria-disabled={!havePicks}
                            >
                                <Pencil className="w-5 h-5 text-gray-600" />
                                {havePicks && (
                                    <span className="text-[10px] text-gray-500 ml-1 align-middle">({selectedCells.length})</span>
                                )}
                            </button>
                        )
                    })()}
                    {/* Add Comment is enabled only when a value cell (not a header) is
                        selected; otherwise the comment has nowhere to anchor and would
                        end up in the history without a cellRef. The disabled state
                        signals the requirement without a popup interrupting the flow. */}
                    {(() => {
                        // Enabled when EITHER a multi-cell selection is in
                        // play OR a single active value cell is picked. The
                        // modal's `onAdd` handler prefers selectedCells and
                        // falls back to active so both paths work.
                        const haveSelection = selectedCells.length > 0
                        const cellPicked = haveSelection || !!(active && active.row >= HEADER_ROWS && active.column >= 1)
                        const hint = haveSelection
                            ? `Add Comment on ${selectedCells.length} selected cell(s)`
                            : (cellPicked ? 'Add Comment' : 'Select a data cell first, then click to add a comment')
                        return (
                            <button
                                type="button"
                                title={hint}
                                onClick={() => {
                                    if (cellPicked) setModal('comment')
                                    else alert('Select one or more data cells first, then click + to add a comment.')
                                }}
                                className={iconBtn(modal === 'comment') + (cellPicked ? '' : ' opacity-40 cursor-not-allowed')}
                                aria-disabled={!cellPicked}
                            >
                                <MessageSquarePlus className="w-5 h-5" />
                                {haveSelection && (
                                    <span className="text-[10px] text-gray-500 ml-1 align-middle">({selectedCells.length})</span>
                                )}
                            </button>
                        )
                    })()}
                    <button type="button" title="Comments" onClick={() => setPanel(panel === 'comments' ? null : 'comments')} className={iconBtn(panel === 'comments')}><MessageSquare className="w-5 h-5 text-gray-600" /></button>
                    <button type="button" title="Version History" onClick={() => setPanel(panel === 'history' ? null : 'history')} className={iconBtn(panel === 'history')}><History className="w-5 h-5 text-gray-600" /></button>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3 flex-1 min-h-0 md:items-stretch">
                <div className="flex flex-col gap-3 flex-1 min-w-0 min-h-0">
                    <div className="da-chart-touch bg-white border border-gray-200 rounded p-3" style={{ minHeight: 240 }}>
                        {hasParams ? (
                            <>
                                <DynamicGraph
                                    projectId={projectId}
                                    includeHidden={true}
                                    selectedParameters={selectedParameters}
                                    dateRange={dateRangeGraph}
                                    timezone={customerTz}
                                    timePeriod={activePeriod}
                                    marks={vstate.marks}
                                    hiddenStatuses={hiddenStatusSet}
                                    refreshNonce={effectiveRefreshNonce}
                                    // Drag on the graph scrolls the grid below to
                                    // the highlighted range instead of zooming.
                                    onRangeSelect={setGraphRange}
                                    selectedRange={graphRange}
                                />
                                {graphRange && (
                                    <div className="mt-2 flex items-center justify-between px-3 py-1.5 bg-[#EBF5FB] border border-[#358ECD]/30 rounded text-xs text-[#4D4D4D]">
                                        <span>
                                            Jumped to{' '}
                                            <span className="font-semibold">{new Date(Math.min(graphRange.from, graphRange.to)).toLocaleString()}</span>
                                            {' '}–{' '}
                                            <span className="font-semibold">{new Date(Math.max(graphRange.from, graphRange.to)).toLocaleString()}</span>
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setGraphRange(null)}
                                            className="ml-3 px-2 py-0.5 text-[#358ECD] hover:bg-white rounded border border-transparent hover:border-[#358ECD] transition"
                                        >
                                            Clear Selection
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="h-48 flex items-center justify-center text-xs text-gray-500">Select parameters in the sidebar to plot a trace.</div>
                        )}
                    </div>
                    <div
                        ref={sheetRef}
                        // Natural height (grows to its rows) with only a HORIZONTAL
                        // scrollbar for wide tables. No vertical overflow here, so the
                        // grid never shows an inner vertical scrollbar — the page
                        // (main content area) provides the single vertical scroll.
                        style={{ minHeight: 240 }}
                        className="da-mobile-scroll bg-white border border-gray-200 overflow-auto max-h-[65vh] md:max-h-none md:flex-1 md:min-h-0 relative"
                        onMouseOver={(e) => {
                            // Delegated hover: only react when the cursor enters a
                            // commented cell. We compute the cell's (paramId, label)
                            // from its position in the rendered table — first td
                            // in each row is the spreadsheet's row-number gutter,
                            // td[1] is matrix col 0 (datetime), so matrixCol = idx - 1.
                            const td = e.target.closest('td.da-comment')
                            if (!td) return
                            const tr = td.parentElement
                            if (!tr) return
                            const cells = Array.from(tr.children)
                            const tdIdx = cells.indexOf(td)
                            const dtCell = cells[1]
                            if (!dtCell || tdIdx < 2) return
                            const label = (dtCell.textContent || '').trim()
                            const matrixCol = tdIdx - 1
                            const param = series[matrixCol - 1]
                            if (!param) return
                            const key = markKey(param.id, label)
                            // Each cell can carry MULTIPLE comments (user-added
                            // and/or the rule's own comment). Collect all and
                            // hand the tooltip an array; it'll stack them in
                            // newest-first order (newest at top of vstate.comments).
                            const userCs = vstate.comments.filter((c) => markKey(c.paramId, c.label) === key)
                            const ruleC  = ruleCommentedKeys.get(key)
                            const entries = [
                                ...userCs.map((c) => ({ kind: 'user', payload: c })),
                                ...(ruleC ? [{ kind: 'rule', payload: ruleC }] : []),
                            ]
                            if (entries.length === 0) return
                            setHoverComment({ entries, cellRef: td.getBoundingClientRect() })
                        }}
                        onMouseOut={(e) => {
                            // Close when the cursor leaves the sheet OR leaves a
                            // commented cell without entering another one.
                            const next = e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('td.da-comment')
                            if (!next) setHoverComment(null)
                        }}
                    >
                        {!hasParams ? (
                            <div className="h-full flex items-center justify-center text-sm text-gray-400 p-8 text-center">Select one or more parameters to validate.</div>
                        ) : loading ? (
                            <div className="h-full flex items-center justify-center gap-2 text-sm text-gray-500">Loading data…</div>
                        ) : (
                            <div style={{ zoom: zoomFactor }} className="dataanalytics-sheet">
                                <Spreadsheet
                                    data={gridData}
                                    onChange={() => {}}
                                    onActivate={(pt) => setActive(pt)}
                                    onSelect={(selection) => {
                                        // react-spreadsheet emits a Selection object — RangeSelection
                                        // has a .range = {start:{row,column}, end:{row,column}}.
                                        // EmptySelection / EntireSelection variants arrive when the
                                        // grid loses focus (e.g. user clicks a status pill outside
                                        // the sheet). Keep the existing selection in that case so
                                        // toolbar actions can still target the picked cells; only
                                        // *replace* the selection when a real range is provided.
                                        const rng = selection && selection.range
                                        if (!rng) return
                                        const r0 = Math.min(rng.start.row, rng.end.row)
                                        const r1 = Math.max(rng.start.row, rng.end.row)
                                        const c0 = Math.min(rng.start.column, rng.end.column)
                                        const c1 = Math.max(rng.start.column, rng.end.column)
                                        const cells = []
                                        for (let r = r0; r <= r1; r++) {
                                            for (let c = c0; c <= c1; c++) {
                                                // Skip header rows + the datetime column.
                                                if (r < HEADER_ROWS || c < 1) continue
                                                cells.push({ row: r, column: c })
                                            }
                                        }
                                        setSelectedCells(cells)
                                    }}
                                />
                            </div>
                        )}
                        {hoverComment && <CommentTooltip data={hoverComment} container={sheetRef.current} />}
                    </div>
                </div>

                {panel === 'comments' && (
                    <CommentsDock
                        // Only show comments that belong to a parameter the
                        // user is currently viewing in the grid. vstate is
                        // global (single localStorage key per user), so
                        // without this filter the sidebar would also list
                        // comments saved earlier under a *different* project's
                        // parameters. The filter is on paramId because every
                        // Parameter row is unique to one device, which is
                        // unique to one project.
                        comments={vstate.comments.filter((c) => series.some((s) => String(s.id) === String(c.paramId)))}
                        onClose={() => setPanel(null)}
                    />
                )}
                {panel === 'history' && <HistoryDock history={vstate.history} onRollback={rollback} onClose={() => setPanel(null)} />}
            </div>

            {modal === 'find' && (
                <FindAndMarkModal series={series} onCancel={() => setModal(null)}
                    onMark={async ({ paramId, condition, v1, v2, status }) => {
                        // Ensure the status pill is ON so marks are visible
                        // immediately — if the user had toggled it off the cells
                        // would be marked but colourless, looking like nothing happened.
                        setEnabledStatuses(prev => ({ ...prev, [status]: true }))
                        // Optimistic local apply — repaints the grid immediately;
                        // server may still reject (e.g. too_large) in which case
                        // we surface an error but keep the local state for now
                        // (next mount reloads from server-side truth).
                        const localCount = applyMark(paramId, condition, v1, v2, status)
                        setModal(null)
                        try {
                            const res = await applyMarkRule({
                                parameterId: paramId,
                                condition, v1, v2, status,
                                fromDate: dateRangeGraph?.from,
                                toDate:   dateRangeGraph?.to,
                                timezone: customerTz,
                            })
                            const n = res.affected_rows
                            // affected_rows = rows actually painted. The
                            // priority guard skips cells already Manually-
                            // marked, so a Find-and-Mark over a heavily-
                            // curated parameter can match many rows but
                            // paint zero. Explain the difference so the
                            // user doesn't think the rule didn't save.
                            const skipped = res?.skipped_due_to_manual || 0
                            const skipNote = skipped > 0
                                ? ` ${skipped} matching cell(s) were already Manually marked and were preserved.`
                                : ''
                            if (n === 0) {
                                alert(`No data points matched the condition in the current date range.${skipNote}`)
                            } else {
                                const scrollNote = ' The grid shows newest data at the top — scroll down to find the coloured rows.'
                                alert(`Marked ${n} point(s) as ${status} (saved to server, rule #${res.rule_id ?? '—'}).${scrollNote}${skipNote}`)
                            }
                        } catch (e) {
                            if (e?.kind === 'too_large') {
                                alert(`Too many points (${e.estimated_count}) for the sync limit. ` +
                                      `Narrow the date range and retry. (Local preview shows ${localCount}.)`)
                            } else {
                                alert(`Server save failed: ${e?.message || e}. ` +
                                      `Local preview shows ${localCount} point(s).`)
                            }
                        }
                    }} />
            )}
            {modal === 'rules' && (
                <MarkRulesModal series={series} onCancel={() => setModal(null)} onChanged={bumpRefresh} />
            )}
            {modal === 'edit-value' && (
                <EditValueModal
                    selectedCount={selectedCells.length}
                    // Snapshot of the values the user is about to overwrite —
                    // shown in the modal so they can confirm before saving.
                    cellPreview={selectedCells.map(({ row, column }) => {
                        const param = series[column - 1]
                        const label = data[row] && data[row][0] && data[row][0].value
                        const value = data[row] && data[row][column] && data[row][column].value
                        return {
                            label,
                            paramName: param?.name || `Col ${column}`,
                            currentValue: value,
                        }
                    }).filter((c) => c.label != null && c.currentValue != null && c.currentValue !== '')}
                    onCancel={() => setModal(null)}
                    onSave={async (newValue) => {
                        // Mirror of markSelectedCellsAs: group selected cells by
                        // parameter id, derive YYYY-MM-DD HH:MM:SS labels in the
                        // customer TZ, POST one bulk update per parameter.
                        const byParam = new Map()
                        for (const { row, column } of selectedCells) {
                            const param = series[column - 1]
                            const label = data[row] && data[row][0] && data[row][0].value
                            if (!param || !label) continue
                            const sampleStr = /:[0-9]{2}$/.test(String(label)) ? `${label}:00` : String(label)
                            if (!byParam.has(param.id)) byParam.set(param.id, [])
                            byParam.get(param.id).push(sampleStr)
                        }
                        if (byParam.size === 0) { alert('No selected cells map to a value column.'); return }
                        let totalAffected = 0
                        try {
                            for (const [paramId, sampleTimes] of byParam.entries()) {
                                const res = await updateCellValues({
                                    parameterId: paramId,
                                    sampleTimes,
                                    newValue,
                                    status:   'corrected',
                                    timezone: customerTz,
                                })
                                totalAffected += (res?.affected_rows || 0)
                            }
                            // Optimistic paint: tag selection as Corrected so the
                            // grid colours update immediately, before the 30 s
                            // marks-poll reconciles. Also seed markDetailsByKey
                            // with the pre-edit value so "Show Original" on the
                            // Corrected pill works right away (won't clobber an
                            // existing original_value if the cell was already
                            // corrected once before).
                            const optimistic = { ...vstate.marks }
                            const detailSeed = []
                            const editedParamNames = [...new Set(
                                selectedCells.map(({ column }) => series[column - 1]?.name).filter(Boolean)
                            )].join(', ')
                            for (const { row, column } of selectedCells) {
                                const param = series[column - 1]
                                const label = data[row] && data[row][0] && data[row][0].value
                                if (!param || !label) continue
                                optimistic[markKey(param.id, label)] = 'corrected'
                                const preVal = data[row]?.[column]?.value
                                if (preVal !== undefined && preVal !== '') {
                                    detailSeed.push({ key: markKey(param.id, label), preVal })
                                }
                            }
                            const editHistText = `Corrected ${totalAffected} cell(s) to ${newValue}${editedParamNames ? ` — ${editedParamNames}` : ''}`
                            setVstate((prev) => {
                                const entry = {
                                    id: uid('h'),
                                    time: now(),
                                    text: editHistText,
                                    snapshot: { marks: optimistic, rules: prev.rules, comments: prev.comments },
                                }
                                const merged = { ...prev, marks: optimistic, history: [entry, ...(prev.history || [])] }
                                saveValidate(merged)
                                return merged
                            })
                            if (detailSeed.length) {
                                setMarkDetailsByKey((prev) => {
                                    const m = new Map(prev)
                                    for (const { key, preVal } of detailSeed) {
                                        if (m.has(key) && m.get(key)?.original_value != null) continue
                                        m.set(key, {
                                            original_value: Number(preVal),
                                            value:          Number(newValue),
                                            status:         'corrected',
                                            rule_id:        null,
                                        })
                                    }
                                    return m
                                })
                            }
                            setModal(null)
                            // Same post-action housekeeping as markSelectedCellsAs /
                            // inline edits: drop the picks now that the operation
                            // they were aimed at has completed.
                            setSelectedCells([])
                            bumpRefresh()
                            alert(`Updated value on ${totalAffected} cell(s). Original values preserved.`)
                        } catch (e) {
                            const msg = e?.response?.data?.message || e?.message || e
                            alert(`Edit failed: ${msg}`)
                        }
                    }}
                />
            )}
            {modal === 'comment' && (
                <AddCommentModal
                    cellLabel={
                        selectedCells.length > 1
                            ? `${selectedCells.length} cells`
                            : (active && active.row >= HEADER_ROWS && active.column >= 1
                                ? `${colLetter(active.column)}${active.row + 1}`
                                : null)
                    }
                    onCancel={() => setModal(null)}
                    onAdd={async (body, attachments) => {
                        // Build the list of (paramId, label, cellRef) tuples
                        // the comment will be attached to. Prefer the multi-
                        // select list when present; fall back to the active
                        // cell so a single-click-then-comment still works.
                        const picks = []
                        const seen = new Set()
                        const addPick = (row, column) => {
                            const key = `${row}|${column}`
                            if (seen.has(key)) return
                            const param = series[column - 1]
                            const label = data[row] && data[row][0] && data[row][0].value
                            if (!param || !label) return
                            seen.add(key)
                            picks.push({
                                paramId: param.id,
                                label,
                                cellRef: `${colLetter(column)}${row + 1}`,
                            })
                        }
                        if (selectedCells.length > 0) {
                            for (const { row, column } of selectedCells) addPick(row, column)
                        } else if (active && active.row >= HEADER_ROWS && active.column >= 1) {
                            addPick(active.row, active.column)
                        }
                        if (picks.length === 0) {
                            alert('Select one or more value cells first, then click "Add Comment".')
                            return
                        }

                        // Group picks by parameter — one API call per parameter
                        // (server creates one Comment row per sample_time in
                        // the request). Same body + same attachment set is
                        // applied to every row in the group.
                        const byParam = new Map() // paramId → [{sampleStr, label, cellRef}, ...]
                        for (const p of picks) {
                            const sampleStr = /:[0-9]{2}$/.test(String(p.label)) ? `${p.label}:00` : String(p.label)
                            if (!byParam.has(p.paramId)) byParam.set(p.paramId, [])
                            byParam.get(p.paramId).push({ sampleStr, label: p.label, cellRef: p.cellRef })
                        }

                        const files = (attachments || [])
                            .map((a) => a && a.file)
                            .filter(Boolean)

                        // Optimistic local entries — one per (paramId, label)
                        // so the grid's blue-triangle indicator paints on
                        // every commented cell right away.
                        const optimisticEntries = picks.map((p) => ({
                            id: uid('c'),
                            author: 'You',
                            cellRef: p.cellRef,
                            paramId: p.paramId,
                            label: p.label,
                            timestamp: now(),
                            body,
                            attachments: attachments || [],
                        }))
                        const summary = picks.length === 1
                            ? `Added a comment on ${picks[0].cellRef}`
                            : `Added a comment on ${picks.length} cells`
                        commit({ ...vstate, comments: [...optimisticEntries, ...vstate.comments] }, summary)
                        setModal(null); setPanel('comments')
                        setSelectedCells([])

                        try {
                            for (const [paramId, items] of byParam.entries()) {
                                await createComment({
                                    parameterId: Number(paramId),
                                    sampleTimes: items.map((i) => i.sampleStr),
                                    cellRefs:    items.map((i) => i.cellRef),
                                    body,
                                    files,
                                    timezone: customerTz,
                                })
                            }
                            // Next 30 s comments-poll reconciles vstate.comments
                            // with server truth (replaces optimistic entries).
                        } catch (e) {
                            const msg = e?.response?.data?.message || e?.message || e
                            alert(`Server save failed: ${msg}. Your comment is shown locally but not persisted across browsers.`)
                        }
                    }}
                />
            )}
        </div>
    )
}

// CommentTooltip — small popover anchored to the hovered cell.
// Rendered via React portal into <body> with position:fixed so it CAN'T be
// clipped by the sheet container's overflow-auto.
//
// Placement preference, in order:
//   1. ABOVE the cell, horizontally centered over the column.
//   2. BELOW the cell, horizontally centered, if there isn't room above.
//   3. RIGHT of the cell when neither above nor below has enough room.
//   4. LEFT of the cell as a last resort.
// In every case the final top/left is clamped into the viewport so the
// tooltip never spills off the edge of the screen.
function CommentTooltip({ data, container }) {
    if (!data || !container) return null
    const entries = data.entries || []
    if (entries.length === 0) return null
    const cellRect = data.cellRef
    const TOOLTIP_W = 288     // matches tailwind w-72 (18rem * 16px)
    const TOOLTIP_MAX_H = 448 // matches max-h-[28rem]
    const GAP = 8
    // Generous typical height — most tooltips are ~250–360px. We only need
    // this rough estimate to decide whether "above" or "below" actually
    // fits; the real height is capped by max-h on the rendered div.
    const ESTIMATED_H = 320

    const vw = window.innerWidth
    const vh = window.innerHeight
    const spaceAbove = cellRect.top
    const spaceBelow = vh - cellRect.bottom
    const spaceRight = vw - cellRect.right
    const spaceLeft  = cellRect.left

    // Center horizontally over the column when stacking vertically.
    const centeredLeft = Math.round(cellRect.left + (cellRect.right - cellRect.left) / 2 - TOOLTIP_W / 2)

    let top, left
    if (spaceAbove >= ESTIMATED_H + GAP) {
        // 1. Above — anchor bottom edge to the cell's top.
        top  = cellRect.top - GAP - Math.min(ESTIMATED_H, spaceAbove - GAP)
        left = centeredLeft
    } else if (spaceBelow >= ESTIMATED_H + GAP) {
        // 2. Below — anchor top edge to the cell's bottom.
        top  = cellRect.bottom + GAP
        left = centeredLeft
    } else if (spaceRight >= TOOLTIP_W + GAP) {
        // 3. Right — vertical centre on the cell.
        top  = Math.round(cellRect.top + (cellRect.bottom - cellRect.top) / 2 - ESTIMATED_H / 2)
        left = cellRect.right + GAP
    } else {
        // 4. Left — vertical centre on the cell.
        top  = Math.round(cellRect.top + (cellRect.bottom - cellRect.top) / 2 - ESTIMATED_H / 2)
        left = Math.max(GAP, cellRect.left - GAP - TOOLTIP_W)
    }
    // Clamp into the viewport.
    top  = Math.max(GAP, Math.min(top,  vh - ESTIMATED_H - GAP))
    left = Math.max(GAP, Math.min(left, vw - TOOLTIP_W   - GAP))

    const renderOne = (entry, i) => {
        const c       = entry.payload || {}
        const isUser  = entry.kind === 'user'
        const author  = isUser ? (c.author    || 'You')          : 'Rule comment'
        const stamp   = isUser ? (c.timestamp || '')             : ''
        const body    = isUser ? (c.body      || '')             : (c.body || '')
        const photos  = isUser ? (c.attachments || []).filter((a) => a?.dataUrl) : []
        const hero    = photos[0]
        const extras  = photos.slice(1)
        return (
            <div key={c.id || i} className={i > 0 ? 'pt-3 mt-3 border-t border-gray-100' : ''}>
                <div className="font-semibold text-sm text-gray-900">{author}</div>
                {stamp && <div className="text-[11px] text-gray-500 mt-0.5">{stamp}</div>}
                {body && <p className="mt-2 whitespace-pre-wrap leading-snug text-[13px] text-[#4D4D4D]">{body}</p>}
                {hero && (
                    // Hero image takes the full tooltip width (per the design
                    // screenshot). object-cover keeps the aspect tidy when the
                    // attached photo is taller than ~200px.
                    <img
                        src={hero.dataUrl}
                        alt={hero.name || 'attachment'}
                        className="mt-3 w-full max-h-56 object-cover rounded border border-gray-200"
                    />
                )}
                {extras.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {extras.slice(0, 3).map((a, k) => (
                            <img key={k} src={a.dataUrl} alt={a.name || 'attachment'}
                                 className="w-12 h-12 object-cover rounded border border-gray-200" />
                        ))}
                        {extras.length > 3 && (
                            <span className="text-[10px] text-gray-500 self-center">+{extras.length - 3} more</span>
                        )}
                    </div>
                )}
            </div>
        )
    }
    // Cap at 5 entries to keep the popover from going off-screen; show count
    // at the bottom if there are more.
    const visible = entries.slice(0, 5)
    // Portal to body so overflow-auto on the sheet container can't clip us.
    return createPortal(
        <div
            role="tooltip"
            style={{
                position: 'fixed', top, left,
                width: TOOLTIP_W, maxHeight: TOOLTIP_MAX_H,
                zIndex: 9999, pointerEvents: 'none',
            }}
            className="overflow-y-auto bg-white border border-gray-200 rounded shadow-lg p-4 text-xs text-[#4D4D4D]"
        >
            {visible.map(renderOne)}
            {entries.length > 5 && (
                <p className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-500">
                    +{entries.length - 5} more comments — open the Comments dock to see all
                </p>
            )}
        </div>,
        document.body
    )
}

// ── Right docks ──
function CommentsDock({ comments, onClose }) {
    const [q, setQ] = useState('')
    const filtered = comments.filter((c) => !q || c.body.toLowerCase().includes(q.toLowerCase()) || c.author.toLowerCase().includes(q.toLowerCase()))
    return (
        // min-h-0 + self-stretch so the list body (flex-1 overflow-auto) scrolls
        // inside the dock instead of growing the page.
        <div className="w-full md:w-72 flex-shrink-0 min-h-0 md:self-stretch bg-white border border-gray-200 rounded flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-bold text-[#4D4D4D]">Comments</h3>
                <button onClick={onClose}><X className="w-4 h-4 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <div className="p-3 border-b border-gray-100">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Comments" className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-[#358ECD]" />
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-4">
                {filtered.length === 0 && <p className="text-xs text-gray-400 text-center mt-6">No comments yet.</p>}
                {filtered.map((c) => (
                    <div key={c.id}>
                        <div className="flex items-baseline gap-2">
                            <span className="text-sm font-semibold text-[#4D4D4D]">{c.author}</span>
                            {c.cellRef && <span className="text-[10px] text-[#358ECD] bg-[#E8F1F8] px-1.5 rounded">{c.cellRef}</span>}
                            <span className="text-[10px] text-gray-400">{c.timestamp}</span>
                        </div>
                        {c.body && <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{c.body}</p>}
                        {c.attachments?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1.5">
                                {c.attachments.map((a, i) => (
                                    <a key={i} href={a.dataUrl} target="_blank" rel="noreferrer">
                                        <img src={a.dataUrl} alt={a.name} className="w-16 h-16 object-cover rounded border border-gray-200 hover:opacity-90" />
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}
function HistoryDock({ history, onRollback, onClose }) {
    return (
        <div className="w-full md:w-72 flex-shrink-0 min-h-0 md:self-stretch bg-white border border-gray-200 rounded flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-bold text-[#4D4D4D]">Version History</h3>
                <button onClick={onClose}><X className="w-4 h-4 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-3">
                {history.length === 0 && <p className="text-xs text-gray-400 text-center mt-6">No changes yet.</p>}
                {history.map((h) => (
                    <div key={h.id} className="group border-l-2 border-[#358ECD] pl-2">
                        <p className="text-[10px] text-gray-400">{h.time}</p>
                        <p className="text-xs text-[#4D4D4D]">{h.text}</p>
                        {h.snapshot && (
                            <button onClick={() => onRollback(h)} className="text-[10px] text-[#358ECD] hover:underline opacity-0 group-hover:opacity-100 transition">
                                Roll back to here
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

// ── Modals ──
// Scrollable overlay: `overflow-y-auto` + top-aligned on mobile so a tall modal
// (e.g. the Add Rule form) is fully reachable instead of having its top/bottom
// clipped off a phone's viewport; centered on desktop as before.
const modalWrap = 'fixed inset-0 bg-black bg-opacity-40 flex items-start md:items-center justify-center z-[10000] p-4 overflow-y-auto'
const inputCls = 'w-full border border-gray-300 rounded px-3 py-2 text-sm text-[#4D4D4D] focus:outline-none focus:border-[#358ECD]'

function EditValueModal({ selectedCount, cellPreview = [], onCancel, onSave }) {
    // When exactly one cell is selected, pre-fill the input with its current
    // value so the user can adjust rather than re-type from scratch.
    const singleCell = cellPreview.length === 1 ? cellPreview[0] : null
    const [value, setValue]   = useState(singleCell ? String(singleCell.currentValue) : '')
    const [busy,  setBusy]    = useState(false)
    const numeric = value !== '' && !isNaN(Number(value))
    const submit = async () => {
        if (!numeric || busy) return
        setBusy(true)
        try { await onSave(Number(value)) }
        finally { setBusy(false) }
    }
    return (
        <div className={modalWrap} onClick={onCancel}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-[#4D4D4D] mb-2">Edit Cell Value</h3>
                <p className="text-xs text-gray-600 mb-3">
                    Setting a new value for <span className="font-semibold">{selectedCount}</span> selected cell(s).
                    The <code className="text-[#4D4D4D]">original_value</code> from the device is preserved — only <code className="text-[#4D4D4D]">value</code> is updated, and each cell is tagged <span className="font-semibold text-green-700">Corrected</span>.
                </p>

                {/* Preview block — shows what will be overwritten so the user
                    can confirm the right cells are about to change. */}
                {cellPreview.length > 0 && (
                    <div className="mb-3 border border-gray-200 rounded bg-gray-50 max-h-32 overflow-auto">
                        <table className="w-full text-[11px]">
                            <thead className="bg-gray-100 sticky top-0">
                                <tr className="text-gray-600">
                                    <th className="text-left px-2 py-1">Datetime</th>
                                    <th className="text-left px-2 py-1">Parameter</th>
                                    <th className="text-right px-2 py-1">Current</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cellPreview.slice(0, 25).map((c, i) => (
                                    <tr key={i} className="border-t border-gray-100">
                                        <td className="px-2 py-1 text-[#4D4D4D]">{c.label}</td>
                                        <td className="px-2 py-1 text-[#4D4D4D]">{c.paramName}</td>
                                        <td className="px-2 py-1 text-right text-gray-900">{c.currentValue}</td>
                                    </tr>
                                ))}
                                {cellPreview.length > 25 && (
                                    <tr><td colSpan={3} className="px-2 py-1 text-center text-gray-500 italic">
                                        … and {cellPreview.length - 25} more
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {singleCell && (
                    <label className="block text-xs text-gray-500 mb-1">
                        Current value: <span className="font-mono text-[#4D4D4D]">{singleCell.currentValue}</span>
                    </label>
                )}
                <label className="block text-sm text-[#4D4D4D] mb-1">New value</label>
                <input
                    className={inputCls + ' mb-2'}
                    placeholder="e.g. 12.34"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
                    autoFocus
                    inputMode="decimal"
                />
                {!numeric && value !== '' && (
                    <div className="text-[11px] text-red-600 mb-3">Enter a numeric value.</div>
                )}
                <button
                    disabled={!numeric || busy}
                    onClick={submit}
                    className="w-full py-2 mt-2 bg-[#358ECD] text-white rounded disabled:opacity-40 hover:bg-[#2d7ab5]"
                >
                    {busy ? 'Saving…' : `SAVE — apply to ${selectedCount} cell(s)`}
                </button>
                <button onClick={onCancel} className="w-full mt-2 text-sm text-[#358ECD] hover:underline">Cancel</button>
            </div>
        </div>
    )
}

function FindAndMarkModal({ series, onCancel, onMark }) {
    const [paramId, setParamId] = useState(series[0]?.id ? String(series[0].id) : '')
    const [condition, setCondition] = useState('between')
    const [v1, setV1] = useState(''); const [v2, setV2] = useState('')
    const [status, setStatus] = useState('')
    const two = CONDITIONS.find((c) => c.id === condition)?.two
    const canMark = paramId && status && v1 !== '' && (!two || v2 !== '')
    return (
        <div className={modalWrap} onClick={onCancel}>
            <div className="cstm-find-mrk bg-white rounded-lg shadow-xl w-full max-w-xl p-5" onClick={(e) => e.stopPropagation()}>
                <h3 className="common-head25 text-lg font-bold text-[#4D4D4D] mb-4 border-b pb-4">Find and Mark</h3>
                <label className="common-size18 block text-sm text-[#4D4D4D] mb-1">Parameter</label>
                <select className={inputCls + ' mb-4'} value={paramId} onChange={(e) => setParamId(e.target.value)}>
                    {!series.length && <option value="">Select Parameter</option>}
                    {series.map((s) => <option key={s.id} value={String(s.id)}>{s.name}{s.unit ? ` (${s.unit})` : ''}</option>)}
                </select>
                <label className="common-size18 block text-sm text-[#4D4D4D] mb-1">Condition</label>
                <select className={inputCls + ' mb-3'} value={condition} onChange={(e) => setCondition(e.target.value)}>
                    {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <div className="flex items-center gap-2 mb-4">
                    <input className={inputCls} placeholder="Value" value={v1} onChange={(e) => setV1(e.target.value)} />
                    {two && <><span className="text-xs text-gray-500">and</span><input className={inputCls} placeholder="Value" value={v2} onChange={(e) => setV2(e.target.value)} /></>}
                </div>
                <label className="common-size18 block text-sm text-[#4D4D4D] mb-1">Mark As</label>
                <select className={inputCls + ' mb-5'} value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Select Status</option>
                    {MARK_AS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <button disabled={!canMark} onClick={() => onMark({ paramId, condition, v1, v2, status })} className="w-full py-2 bg-[#358ECD] text-white rounded disabled:opacity-40 hover:bg-[#2d7ab5]">MARK</button>
                <button onClick={onCancel} className="w-full mt-2 text-sm text-[#358ECD] hover:underline">Cancel</button>
            </div>
        </div>
    )
}

// MarkRulesModal — manages ON-GOING rules (rule_type='on_going'). The cron job
// `evaluate_pending_marks` applies these to new Data rows as they arrive
// (every 5 minutes). Find-and-Mark one-time rules are NOT shown here.
function MarkRulesModal({ series, onCancel, onChanged }) {
    const [list, setList]       = useState([])         // [{id, parameter_id, condition, v1, v2, status, comment, enabled, affected_rows, created_at, ...}]
    const [loading, setLoading] = useState(true)
    const [editing, setEditing] = useState(null)       // null | {paramId, condition, v1, v2, status, comment}
    const [saving, setSaving]   = useState(false)
    const [error, setError]     = useState(null)

    const reload = async () => {
        setLoading(true); setError(null)
        try {
            const aggregated = []
            for (const s of series) {
                if (!s?.id) continue
                // includeDisabled: true so the modal shows every rule the
                // user has ever created — active AND inactive — and the
                // toggle below lets them flip enabled state without losing
                // the row from view. Without this, disabling a rule made it
                // vanish from the list with no way to re-enable.
                const res = await fetchRules({ parameterId: s.id, ruleType: 'on_going', includeDisabled: true })
                // Stamp parameter_id from the fetch context as a fallback in case
                // an older backend doesn't include it in the response body —
                // the Edit form reads `rule.parameter_id` to pre-select the parameter.
                for (const r of (res?.rules || [])) aggregated.push({ ...r, parameter_id: r.parameter_id ?? s.id, _paramName: s.name })
            }
            // Newest first across all parameters.
            aggregated.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
            setList(aggregated)
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || 'Failed to load rules')
        } finally { setLoading(false) }
    }
    useEffect(() => { reload() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [series.map(s => s.id).join(',')])

    // `editing` carries an optional `id`:
    //   id missing → "+ Add Rule" mode → POST /rules/create/
    //   id present → "Edit"     mode → PATCH /rules/<id>/  (parameter is immutable;
    //                                  the select stays disabled while editing)
    const blank = () => ({
        paramId:   series[0]?.id ? String(series[0].id) : '',
        name:      '',
        condition: 'between', v1: '', v2: '', status: 'erroneous', comment: '',
    })
    const editingExisting = (rule) => ({
        id:        rule.id,
        paramId:   String(rule.parameter_id ?? ''),
        name:      rule.name || '',
        condition: rule.condition,
        v1:        rule.v1 != null ? String(rule.v1) : '',
        v2:        rule.v2 != null ? String(rule.v2) : '',
        status:    rule.status,
        comment:   rule.comment || '',
    })
    const two = editing && CONDITIONS.find((c) => c.id === editing.condition)?.two

    const onSave = async () => {
        if (!editing.paramId) { setError('Select a parameter'); return }
        setSaving(true); setError(null)
        try {
            if (editing.id) {
                // PATCH only mutable fields; parameter_id is immutable.
                await patchRule(editing.id, {
                    name:      editing.name || null,
                    condition: editing.condition,
                    v1:        editing.v1 !== '' ? Number(editing.v1) : null,
                    v2:        editing.v2 !== '' ? Number(editing.v2) : null,
                    status:    editing.status,
                    comment:   editing.comment || null,
                })
            } else {
                await createOnGoingRule({
                    parameterId: Number(editing.paramId),
                    name:        editing.name || null,
                    condition:   editing.condition,
                    v1:          editing.v1 !== '' ? Number(editing.v1) : null,
                    v2:          editing.v2 !== '' ? Number(editing.v2) : null,
                    status:      editing.status,
                    comment:     editing.comment || null,
                })
            }
            setEditing(null)
            await reload()
            // Creating/editing a rule changes which cells are marked — re-pull
            // the grid marks so the change shows without leaving the tab.
            onChanged?.()
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || 'Failed to save rule')
        } finally { setSaving(false) }
    }

    const toggle = async (rule) => {
        try { await patchRule(rule.id, { enabled: !rule.enabled }); await reload(); onChanged?.() }
        catch (e) { setError(e?.response?.data?.message || e?.message || 'Toggle failed') }
    }
    const remove = async (rule) => {
        if (!window.confirm(`Delete this rule? It will be soft-deleted (audit history is preserved).`)) return
        // onChanged re-pulls the grid marks: the deleted rule is now
        // enabled=False, so list_marks drops its cells and they un-paint
        // right away instead of only after leaving and re-entering the tab.
        try { await deleteRule(rule.id); await reload(); onChanged?.() }
        catch (e) { setError(e?.response?.data?.message || e?.message || 'Delete failed') }
    }

    const condLabel = (id) => CONDITIONS.find((c) => c.id === id)?.label || id
    const statusLabel = (id) => STATUS_CONFIG.find((s) => s.id === id)?.label || id

    return (
        <div className={modalWrap} onClick={onCancel}>
            <div className="cstm-find-mrk bg-white rounded-lg shadow-xl w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <div>
                        <h3 className="common-head25 text-lg font-bold text-[#4D4D4D] md:mb-2">
                            {/* Mobile mirrors the XD: the header names the current
                                step (Add/Edit Rule); desktop keeps "Mark Rules". */}
                            <span className="md:hidden">{editing ? (editing.id ? 'Edit Rule' : 'Add Rule') : 'Mark Rules'}</span>
                            <span className="hidden md:inline">Mark Rules</span>
                        </h3>
                        {/* Explainer is desktop-only — the mobile XD shows just the list. */}
                        <p className="hidden md:block common-size15 text-xs text-gray-500 mt-0.5">
                            On-going rules — applied automatically to <em>new</em> incoming data
                            (every 5 min). They do not retro-tag rows that arrived before the rule
                            was created — for that, use Find&nbsp;and&nbsp;Mark.
                        </p>
                    </div>
                    {/* Desktop closes from here; mobile uses the SAVE/Cancel footer (XD). */}
                    <button onClick={onCancel} className="hidden md:inline-block px-3 py-1 border border-gray-300 text-[#4D4D4D] text-sm rounded hover:bg-gray-50">Close</button>
                </div>

                <div className="px-5 py-4">
                    {error && <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

                    {!editing ? (
                        <>
                            <div className="flex items-center justify-between mb-2">
                                <p className="common-size15 text-sm font-bold text-[#4D4D4D]">
                                    <span className="md:hidden">Rules</span>
                                    <span className="hidden md:inline">Configured rules</span>
                                </p>
                                <button onClick={() => setEditing(blank())} disabled={!series.length}
                                        className="common-size15 px-3 py-1.5 border-2 border-[#358ECD] font-semibold text-[#358ECD] text-sm rounded-lg hover:bg-[#E8F1F8] disabled:opacity-50 disabled:cursor-not-allowed">
                                    <span className="md:hidden">Add Rule</span>
                                    <span className="hidden md:inline">+ Add Rule</span>
                                </button>
                            </div>
                            <div className="hidden md:block border border-gray-200 rounded overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                                        <tr>
                                            <th className="text-[#4D4D4D] text-left  px-3 py-2">Parameter</th>
                                            <th className="text-[#4D4D4D] text-left  px-3 py-2">Condition</th>
                                            <th className="text-[#4D4D4D] text-left  px-3 py-2">Value(s)</th>
                                            <th className="text-[#4D4D4D] text-left  px-3 py-2">Mark&nbsp;As</th>
                                            <th className="text-[#4D4D4D] text-right px-3 py-2">Tagged so far</th>
                                            <th className="text-[#4D4D4D] text-center px-3 py-2">Enabled</th>
                                            <th className="text-[#4D4D4D] text-right px-3 py-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {loading && <tr><td colSpan={7} className="p-4 text-center text-xs text-gray-400">Loading…</td></tr>}
                                        {!loading && list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-xs text-gray-400">No on-going rules yet. Click <em>+ Add Rule</em> to create one.</td></tr>}
                                        {!loading && list.map((r) => (
                                            <tr key={r.id} className={r.enabled ? '' : 'opacity-50'}>
                                                <td className="px-3 py-2 text-[#4D4D4D]">{r.name || r._paramName || `#${r.parameter_id}`}</td>
                                                <td className="px-3 py-2 text-[#4D4D4D]">{condLabel(r.condition)}</td>
                                                <td className="px-3 py-2 text-[#4D4D4D]">
                                                    {r.v1}{r.v2 != null ? ` … ${r.v2}` : ''}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className="inline-block px-2 py-0.5 rounded text-xs"
                                                          style={{ backgroundColor: STATUS_CONFIG.find(s => s.id === r.status)?.bg || '#eee',
                                                                   color: STATUS_CONFIG.find(s => s.id === r.status)?.text || '#333' }}>
                                                        {statusLabel(r.status)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-right text-[#4D4D4D]">{r.affected_rows}</td>
                                                <td className="px-3 py-2 text-center">
                                                    {/* Switch-style toggle — click anywhere on the
                                                        track or thumb to flip the rule's enabled state.
                                                        role="switch" + aria-checked makes it readable to
                                                        screen readers; sr-only label clarifies what's
                                                        being toggled. */}
                                                    <button
                                                        type="button"
                                                        role="switch"
                                                        aria-checked={r.enabled}
                                                        aria-label={`${r.enabled ? 'Disable' : 'Enable'} rule for ${r._paramName || `#${r.parameter_id}`}`}
                                                        onClick={() => toggle(r)}
                                                        title={r.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                                                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#358ECD]/40 ${r.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                                                    >
                                                        <span
                                                            aria-hidden="true"
                                                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${r.enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}
                                                        />
                                                    </button>
                                                </td>
                                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                                    <button onClick={() => setEditing(editingExisting(r))}
                                                            className="text-[#4D4D4D] text-xs hover:underline mr-3">Edit</button>
                                                    <button onClick={() => remove(r)} className="text-[#4D4D4D] text-xs hover:underline">Delete</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile: a simple card list (no wide table) — matches the XD.
                                Two rules can share one parameter (e.g. Low/High threshold),
                                so the condition line is shown to tell them apart. */}
                            <div className="md:hidden flex flex-col gap-2">
                                {loading && <p className="p-4 text-center text-xs text-gray-400">Loading…</p>}
                                {!loading && list.length === 0 && (
                                    <p className="p-6 text-center text-xs text-gray-400">No on-going rules yet. Tap <em>+ Add Rule</em> to create one.</p>
                                )}
                                {!loading && list.map((r) => (
                                    <div key={r.id} className={`flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2.5 ${r.enabled ? '' : 'opacity-50'}`}>
                                        <div className="min-w-0 pr-2">
                                            <p className="text-sm text-[#4D4D4D] truncate">{r.name || r._paramName || `#${r.parameter_id}`}</p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {condLabel(r.condition)} {r.v1}{r.v2 != null ? ` … ${r.v2}` : ''} → {statusLabel(r.status)}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-4 shrink-0">
                                            <button type="button" onClick={() => remove(r)} aria-label="Delete rule" title="Delete rule" className="text-red-500 hover:text-red-600">
                                                <Trash2 className="w-[18px] h-[18px]" />
                                            </button>
                                            <button type="button" onClick={() => setEditing(editingExisting(r))} aria-label="Edit rule" title="Edit rule" className="text-gray-500 hover:text-gray-700">
                                                <Pencil className="w-[18px] h-[18px]" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Mobile footer — rules are saved individually as they're
                                added/edited/deleted, so SAVE just closes the sheet
                                (matches the XD's SAVE / Cancel). */}
                            <div className="md:hidden mt-5 flex flex-col items-center gap-3">
                                <button type="button" onClick={onCancel}
                                        className="w-full bg-[#358ECD] text-white font-semibold py-2.5 rounded-lg hover:bg-[#2d7ab5]">
                                    SAVE
                                </button>
                                <button type="button" onClick={onCancel} className="text-[#358ECD] text-sm underline">Cancel</button>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-3">
                            {/* Desktop keeps the inline sub-heading; on mobile the header
                                title already reads "Add Rule"/"Edit Rule" (XD). */}
                            <p className="hidden md:block text-sm font-semibold text-[#4D4D4D]">
                                {editing.id ? `Edit rule #${editing.id}` : 'New on-going rule'}
                            </p>

                            <div>
                                <label className="block common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Rule Name</label>
                                <input className={inputCls} placeholder="Leave Blank for Default"
                                       value={editing.name}
                                       onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                            </div>

                            <div>
                                <label className="block common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Parameter</label>
                                {/* Parameter is immutable on edit — backend ignores parameter_id on PATCH. */}
                                <select className={inputCls} value={editing.paramId}
                                        disabled={!!editing.id}
                                        onChange={(e) => setEditing({ ...editing, paramId: e.target.value })}>
                                    {series.map((s) => <option key={s.id} value={String(s.id)}>{s.name}{s.unit ? ` (${s.unit})` : ''}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="block common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Condition</label>
                                <select className={inputCls} value={editing.condition} onChange={(e) => setEditing({ ...editing, condition: e.target.value })}>
                                    {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                                </select>
                                <div className="flex items-center gap-2 mt-2">
                                    <input className={inputCls} placeholder="Value" value={editing.v1}
                                           onChange={(e) => setEditing({ ...editing, v1: e.target.value })} />
                                    {two && <>
                                        <span className="text-xs text-gray-500">and</span>
                                        <input className={inputCls} placeholder="Value" value={editing.v2}
                                               onChange={(e) => setEditing({ ...editing, v2: e.target.value })} />
                                    </>}
                                </div>
                            </div>

                            <div>
                                <label className="block common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Mark As</label>
                                <select className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                                    {MARK_AS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                                </select>
                            </div>

                            {/* Comment — desktop only (the mobile XD omits it). */}
                            <div className="hidden md:block">
                                <label className="block common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Comment</label>
                                <textarea className={inputCls} rows={2} placeholder="Comment (optional)"
                                          value={editing.comment}
                                          onChange={(e) => setEditing({ ...editing, comment: e.target.value })} />
                            </div>

                            {/* Desktop footer: right-aligned Cancel / SAVE RULE. */}
                            <div className="hidden md:flex justify-end gap-2 pt-1">
                                <button onClick={() => setEditing(null)} disabled={saving}
                                        className="px-3 py-1.5 border border-gray-300 text-[#4D4D4D] text-sm rounded">Cancel</button>
                                <button onClick={onSave} disabled={saving}
                                        className="px-3 py-1.5 bg-[#358ECD] text-white text-sm rounded disabled:opacity-50">
                                    {saving ? 'Saving…' : 'SAVE RULE'}
                                </button>
                            </div>

                            {/* Mobile footer: full-width SAVE + Cancel link (XD). Cancel
                                returns to the rule list rather than closing the sheet. */}
                            <div className="md:hidden mt-5 flex flex-col items-center gap-3">
                                <button onClick={onSave} disabled={saving}
                                        className="w-full bg-[#358ECD] text-white font-semibold py-2.5 rounded-lg hover:bg-[#2d7ab5] disabled:opacity-50">
                                    {saving ? 'Saving…' : 'SAVE'}
                                </button>
                                <button onClick={() => setEditing(null)} disabled={saving} className="text-[#358ECD] text-sm underline">Cancel</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

function AddCommentModal({ cellLabel, onCancel, onAdd }) {
    const [body, setBody] = useState('')
    const [attachments, setAttachments] = useState([])
    const [over, setOver] = useState(false)
    const fileRef = useRef(null)

    const addFiles = (files) => {
        Array.from(files || []).forEach((f) => {
            // Server accepts images AND video — broaden the client gate to match.
            if (!f.type.startsWith('image/') && !f.type.startsWith('video/')) {
                alert(`"${f.name}" must be an image or video.`); return
            }
            // Server caps per-file at 25 MB — keep the modal in sync so users
            // don't get a confusing late rejection after the upload starts.
            if (f.size > 25 * 1024 * 1024) {
                alert(`"${f.name}" is larger than 25 MB (server limit). Please pick a smaller file.`); return
            }
            // Build a thumbnail dataUrl for instant preview; ALSO keep the raw
            // File so we can hand it to the multipart upload as-is.
            const reader = new FileReader()
            reader.onload = () => setAttachments((a) => [...a, { name: f.name, dataUrl: reader.result, file: f, mime: f.type, bytes: f.size }])
            reader.readAsDataURL(f)
        })
    }

    return (
        <div className={modalWrap} onClick={onCancel}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-base font-bold text-[#4D4D4D] mb-3">Add Comment{cellLabel ? ` — ${cellLabel}` : ''}</h3>
                <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Comment" className={inputCls + ' resize-none mb-2'} />
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = '' }} />
                <div
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setOver(true) }}
                    onDragLeave={() => setOver(false)}
                    onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files) }}
                    className={`border border-dashed rounded p-4 text-center text-xs cursor-pointer mb-2 transition ${over ? 'border-[#358ECD] bg-[#E8F1F8] text-[#358ECD]' : 'border-gray-300 text-gray-400 hover:bg-gray-50'}`}>
                    Drag Attachments or Upload Photos
                </div>
                {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                        {attachments.map((a, i) => (
                            <div key={i} className="relative">
                                <img src={a.dataUrl} alt={a.name} className="w-14 h-14 object-cover rounded border border-gray-200" />
                                <button onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-white border border-gray-300 rounded-full w-4 h-4 flex items-center justify-center text-gray-500 hover:text-red-500"><X className="w-2.5 h-2.5" /></button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex justify-end gap-2">
                    <button onClick={onCancel} className="px-4 py-1.5 border border-gray-300 text-[#4D4D4D] text-sm rounded">CANCEL</button>
                    <button disabled={!body.trim() && attachments.length === 0} onClick={() => onAdd(body.trim(), attachments)} className="px-4 py-1.5 bg-[#358ECD] text-white text-sm rounded disabled:opacity-40">COMMENT</button>
                </div>
            </div>
        </div>
    )
}
